/**
 * Permission helper utilities for ACL system
 */
import qrMenuItems from "@/data/menu"

/**
 * Check if user has a specific permission
 * @param {Array} userPermissions - Array of user permissions
 * @param {String} permission - Permission to check
 * @returns {Boolean}
 */
export function hasPermission(userPermissions, permission) {
    if (!userPermissions || !Array.isArray(userPermissions)) {
        return false
    }
    return userPermissions.includes(permission)
}

/**
 * Check if user has head menu permission (e.g., "ticketmaster")
 * Head menu name is converted to lowercase for comparison
 * @param {Array} userPermissions - Array of user permissions
 * @param {String} headMenuName - Head menu name (e.g., "TicketMaster" or "ticketmaster")
 * @returns {Boolean}
 */
export function hasHeadMenuPermission(userPermissions, headMenuName) {
    if (!headMenuName) return false
    const normalizedName = headMenuName.toLowerCase()
    return hasPermission(userPermissions, normalizedName)
}

/**
 * Check if user has sub menu permission
 * Requires both head permission and sub permission
 * @param {Array} userPermissions - Array of user permissions
 * @param {String} headMenuName - Head menu name (e.g., "TicketMaster")
 * @param {String} subPermission - Sub permission (e.g., "ticketmaster-refresh")
 * @returns {Boolean}
 */
export function hasSubMenuPermission(userPermissions, headMenuName, subPermission) {
    if (!subPermission) return false
    
    // Check if head menu permission exists
    const hasHead = hasHeadMenuPermission(userPermissions, headMenuName)
    if (!hasHead) return false
    
    // Check if sub permission exists
    return hasPermission(userPermissions, subPermission)
}

/**
 * Filter menu items based on permissions
 * @param {Array} menuItems - Array of menu items
 * @param {Array} userPermissions - Array of user permissions
 * @returns {Array} Filtered menu items
 */
export function filterMenuItemsByPermissions(menuItems, userPermissions) {
    if (!menuItems || !Array.isArray(menuItems)) {
        return []
    }
    
    // Ensure userPermissions is an array
    const permissions = Array.isArray(userPermissions) ? userPermissions : []
    
    return menuItems
        .map(item => {
            // Always show Home/root menu without permission check
            // Note: many top-level menus are marked as isRoot in menu.js; do NOT treat isRoot as "Home".
            const isHomeMenu = item.id === '/' || item.name === 'root'
            if (isHomeMenu) {
                return item
            }
            
            // Check if head menu should be shown
            // If head menu has permission field, check it. Otherwise, show by default.
            let hasHead = true // Default: show if no permission defined
            
            if (item.permission) {
                // If permission is explicitly defined on head menu, check it
                hasHead = hasPermission(permissions, item.permission)
            }
            // If no permission field, show by default (hasHead = true)
            
            if (!hasHead) {
                return null // Hide entire menu if no head permission
            }
            
            // If no children, return item as is
            if (!item.child || !Array.isArray(item.child)) {
                return item
            }
            
            // Filter children based on sub permissions
            const headMenuName = item.name || item.id
            const filteredChildren = item.child.filter(child => {
                // If child has permission field, check it
                if (child.permission) {
                    return hasPermission(permissions, child.permission)
                }
                
                // If child has no permission field, show it by default
                return true
            })
            
            // If no children left after filtering, don't show the parent menu
            if (filteredChildren.length === 0) {
                return null
            }
            
            // Return item with filtered children
            return {
                ...item,
                child: filteredChildren
            }
        })
        .filter(item => item !== null) // Remove null items
}

/**
 * Find menu item by route name or path
 * @param {String} routeName - Route name (e.g., 'oss-web')
 * @param {String} routePath - Route path (e.g., '/ticketmaster/am-web')
 * @returns {Object|null} Menu item or null
 */
function findMenuItemByRoute(routeName, routePath) {
    if (!qrMenuItems || !Array.isArray(qrMenuItems)) {
        return null
    }
    
    for (const menuItem of qrMenuItems) {
        // Check if it's the root menu
        if (routePath === '/' || routeName === 'home' || routeName === 'Welcome') {
            if (menuItem.id === '/' || menuItem.name === 'root') {
                return menuItem
            }
        }
        
        // Check children
        if (menuItem.child && Array.isArray(menuItem.child)) {
            for (const child of menuItem.child) {
                // Match by name or id (path)
                if (child.name === routeName || child.id === routePath) {
                    return {
                        ...child,
                        headMenuName: menuItem.name || menuItem.id
                    }
                }
            }
        }
    }
    
    return null
}

/**
 * Check if user has permission to access a route
 * @param {Array} userPermissions - Array of user permissions
 * @param {String} routeName - Route name (e.g., 'oss-web')
 * @param {String} routePath - Route path (e.g., '/ticketmaster/am-web')
 * @returns {Boolean} True if user has permission to access the route
 */
export function canAccessRoute(userPermissions, routeName, routePath) {
    // Root/Home routes are always accessible
    if (routePath === '/' || routeName === 'home' || routeName === 'Welcome') {
        return true
    }
    
    // Find menu item for this route
    const menuItem = findMenuItemByRoute(routeName, routePath)
    
    // If menu item not found, allow access (backward compatibility)
    if (!menuItem) {
        return true
    }
    
    // If menu item has headMenuName, it's a child menu
    if (menuItem.headMenuName) {
        // Find head menu item to check its permission field
        const headMenuItem = qrMenuItems.find(item => (item.name || item.id) === menuItem.headMenuName)
        
        // Check head menu: if no permission field, allow access
        if (headMenuItem && !headMenuItem.permission) {
            // Head menu has no permission field, check child permission
            if (!menuItem.permission) {
                return true // Both head and child have no permission field
            }
            // Child has permission field, check it
            return hasPermission(userPermissions, menuItem.permission)
        }
        
        // Head menu has permission field, check it
        if (headMenuItem && headMenuItem.permission) {
            const hasHead = hasPermission(userPermissions, headMenuItem.permission)
            if (!hasHead) {
                return false
            }
        } else {
            // Fallback: check head menu permission (backward compatibility)
            const hasHead = hasHeadMenuPermission(userPermissions, menuItem.headMenuName)
            if (!hasHead) {
                return false
            }
        }
        
        // If child has no permission field, allow access
        if (!menuItem.permission) {
            return true
        }
        
        // Check child permission
        return hasPermission(userPermissions, menuItem.permission)
    }
    
    // For root level menus, check if permission field exists
    if (!menuItem.permission) {
        // No permission field, allow access by default
        return true
    }
    
    // Permission field exists, check it
    return hasPermission(userPermissions, menuItem.permission)
}

/**
 * Check if user has any of the specified permissions
 * @param {Array} userPermissions - Array of user permissions
 * @param {Array} permissions - Array of permissions to check
 * @returns {Boolean} True if user has at least one of the permissions
 */
export function hasAnyPermission(userPermissions, permissions) {
    if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
        return false
    }
    return permissions.some(permission => hasPermission(userPermissions, permission))
}

/**
 * Check if user has all of the specified permissions
 * @param {Array} userPermissions - Array of user permissions
 * @param {Array} permissions - Array of permissions to check
 * @returns {Boolean} True if user has all of the permissions
 */
export function hasAllPermissions(userPermissions, permissions) {
    if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
        return false
    }
    return permissions.every(permission => hasPermission(userPermissions, permission))
}
